import { zodResolver } from '@hookform/resolvers/zod'
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Icon,
  Input,
  LinkButton
} from '@shopflo/ui'
import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useRouter } from 'next/router'
import { useAuth } from 'src/lib/contexts/AuthContext'
import { useFilter } from 'src/lib/contexts/FilterContext'
import { useLocale } from 'src/lib/hooks/useLocal'
import { errorToast, successToast } from 'src/lib/utils/toasters'
import { postRequest } from 'src/lib/core/api-client'
import { apiURI, constants } from 'src/lib/utils/constants'
import { getXShopfloVersion } from 'src/lib/core/api-methods'
import { refresh, setVersionCookie } from 'src/lib/utils/helpers'
import useReCaptcha from 'src/lib/hooks/useReCaptcha'
import OTPInput from '../common/OtpInput'
import Cookies from 'js-cookie'
import { loginFormSchema, LoginFormValues, defaultLoginFormValues } from 'src/@core/schemas/login-schema'
import { ArrowLeft, PaperPlaneRight, Eye, EyeSlash } from '@phosphor-icons/react'
import { useOTPCounter } from '../../lib/hooks/useOtpCounter'
import { useUser } from 'src/lib/contexts/UserContext'
import { getInitialAccessibleTabPath, filterNavigationItemsByPermission } from 'src/lib/utils/navigation-rbac'
import { createNavigationMenuItems } from '../navigation/nav-items'
import { useOrganisationContext } from 'src/lib/contexts/OrganisationContext'

const LoginPageV2 = () => {
  const { t } = useLocale()
  const { login } = useAuth()
  const { setFiltersCtx } = useFilter()
  const { generateReCaptchaToken } = useReCaptcha()
  const router = useRouter()
  const { user, isUserConfigured, mutateUser } = useUser()
  const {
    getters: { isShopConnected, hasOverdueInvoices }
  } = useOrganisationContext()

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [showPassword, setShowPassword] = useState<boolean>(false)
  const [showOTPPage, setShowOTPPage] = useState<boolean>(false)
  const [pendingRedirect, setPendingRedirect] = useState<boolean>(false)

  // Once user profile loads after login, perform the RBAC-aware redirect.
  useEffect(() => {
    if (!pendingRedirect || !user) return
    setPendingRedirect(false)
    const allNavItems = createNavigationMenuItems(t, isShopConnected, isUserConfigured, hasOverdueInvoices)
    const accessibleNavItems = filterNavigationItemsByPermission(allNavItems, user)
    // The home item carries TAB_COPILOT, so this lands copilot users on /home and everyone else on their first permitted tab
    router.replace(getInitialAccessibleTabPath(accessibleNavItems))
  }, [pendingRedirect, user])

  const [otpState, setOtpState] = useState({
    value: '',
    error: false,
    errorMessage: '',
    context: ''
  })

  const {
    count: otpCounter,
    start: startOtpCounter,
    reset: resetOtpCounter
  } = useOTPCounter({
    initialValue: 30
  })

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginFormSchema),
    defaultValues: defaultLoginFormValues
  })

  const checkForVersion = async () => {
    try {
      const versionResponse: any = await getXShopfloVersion()
      if (versionResponse?.data?.success) {
        if (Cookies.get(constants.FLO_SHOPFLO_VERSION) !== versionResponse?.data?.data?.version) {
          setVersionCookie(versionResponse?.data?.data?.version ?? 'stable')
          refresh()
        }
      } else {
        throw new Error(t('something_went_wrong'))
      }
    } catch (err) {
      setVersionCookie('stable')
      console.error(err)
    }
  }

  const validateOTP = async (otp: string) => {
    try {
      setIsLoading(true)

      const response: any = await postRequest(
        '/merchant-user/verify',
        apiURI.AUTH_ENGINE,
        {
          otp,
          context_id: otpState.context
        },
        true
      )

      if (!response?.data?.success) {
        throw new Error(response?.data?.error?.message || t('something_went_wrong'))
      }

      const responseData = response?.data?.data
      const formData = form.getValues()

      const authTokens = {
        accessToken: responseData?.access_token,
        accessTokenExpiry: responseData?.access_token_expires_at,
        refreshToken: responseData?.refresh_token,
        refreshTokenExpiry: responseData?.refresh_token_expires_at,
        intercomToken: responseData?.intercom_jwttoken
      }

      login({
        idToken: authTokens.accessToken,
        email: formData.email,
        refreshToken: authTokens.refreshToken,
        authExpiry: authTokens.accessTokenExpiry,
        refreshExpiry: authTokens.refreshTokenExpiry,
        intercomToken: authTokens.intercomToken
      })
      checkForVersion()
      setFiltersCtx()

      // The profile SWR cached null while logged out and never revalidates on its own —
      // without this the pendingRedirect effect below never fires and login stalls on this page.
      mutateUser()
      setPendingRedirect(true)
    } catch (error: any) {
      setOtpState(prev => ({
        ...prev,
        error: true,
        errorMessage: error?.message || t('something_went_wrong')
      }))
    } finally {
      setIsLoading(false)
    }
  }

  const validateCredentials = async () => {
    const formData = form.getValues()

    try {
      setIsLoading(true)

      const payload = {
        email: formData.email.trim(),
        password: formData.password,
        captcha_token: ''
      }
      const response: any = await postRequest('/merchant-user/login', apiURI.AUTH_ENGINE, payload, true)

      if (!response?.data?.success) {
        throw new Error(response?.data?.error?.message || t('something_went_wrong'))
      }

      const responseData = response?.data?.data
      if (responseData?.otp_required) {
        setOtpState(prev => ({ ...prev, context: responseData?.context_id }))
        setShowOTPPage(true)
        setIsLoading(false)
        startOtpCounter()
        return
      }

      const authTokens = {
        accessToken: responseData?.access_token,
        accessTokenExpiry: responseData?.access_token_expires_at,
        refreshToken: responseData?.refresh_token,
        refreshTokenExpiry: responseData?.refresh_token_expires_at,
        intercomToken: responseData?.intercom_jwttoken
      }

      login({
        idToken: authTokens.accessToken,
        email: formData.email,
        refreshToken: authTokens.refreshToken,
        authExpiry: authTokens.accessTokenExpiry,
        refreshExpiry: authTokens.refreshTokenExpiry,
        intercomToken: authTokens.intercomToken
      })
      checkForVersion()
      setFiltersCtx()

      // The profile SWR cached null while logged out and never revalidates on its own —
      // without this the pendingRedirect effect below never fires and login stalls on this page.
      mutateUser()
      setPendingRedirect(true)
    } catch (error: any) {
      errorToast(error?.message || t('something_went_wrong'))
    } finally {
      setIsLoading(false)
    }
  }

  const handleResendOtp = async () => {
    setIsLoading(true)
    try {
      const response: any = await postRequest('/merchant-user/resend', apiURI.AUTH_ENGINE, {
        context_id: otpState.context
      })
      if (!response?.data?.success) {
        setOtpState(prev => ({ ...prev, errorMessage: t('something_went_wrong'), error: true }))
        setIsLoading(false)
        return
      }
      startOtpCounter()
      setOtpState(prev => ({ ...prev, error: false, errorMessage: '' }))
      successToast(t('otp_sent'))
    } catch (error) {
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }

  if (showOTPPage) {
    return (
      <div className='flex flex-col justify-between w-auto rounded-2xl px-4 gap-16'>
        <div className='flex w-full'>
          <h3 className='text-2xl text-zinc-700 font-semibold text-center w-full'>
            {t('login_page.enter_verification_code')}
          </h3>
        </div>
        <div className='flex flex-col gap-12    '>
          <div className='flex flex-col gap-1'>
            <p className='text-sm font-regular text-center text-zinc-600'>
              {t('login_page.we_ve_sent_a_code')} <span className='text-text-sm-medium'>{form.getValues('email')}</span>
            </p>
            <p className='text-text-sm-regular text-center text-coal-dark flex items-center w-full justify-center gap-2'>
              <span className='text-zinc-600'>{t('login_page.did_not_receive_code')}</span>
              {Boolean(otpCounter) ? (
                <span className='text-text-sm-regular flex items-center gap-1 text-zinc-600'>
                  {t('login_page.resend_otp_in_prefix')}
                  <span className='ui-font-bold ui-text-primary'>{otpCounter}</span>
                  {t('login_page.resend_otp_in_suffix')}
                </span>
              ) : (
                <LinkButton
                  variant='primary'
                  size='xs'
                  onClick={handleResendOtp}
                  leftIcon={<Icon IconComponent={PaperPlaneRight} size='sm'></Icon>}
                >
                  {t('login_page.resend_otp')}
                </LinkButton>
              )}
            </p>
          </div>
          <div className='flex flex-col gap-3'>
            <OTPInput
              OTPLength={6}
              value={otpState.value}
              onChange={value => setOtpState(prev => ({ ...prev, value }))}
              hasError={(otpState.error && !isLoading) || false}
              autoFocus={true}
              inputType={'tel'}
              disabled={false}
              isLoading={isLoading}
              onSubmit={(otp: string) => {
                setOtpState(prev => ({ ...prev, error: false, errorMessage: '' }))
                validateOTP(otp)
              }}
            />
            {otpState.error && <span className='text-sm text-center text-red-dark m'>{otpState.errorMessage}</span>}
          </div>
        </div>
        <div className='text-sm text-center  '>
          <p
            className='text-carbon inline-flex items-center justify-center text-center gap-2 hover:bg-zinc-400/10 py-2 px-3 rounded-md cursor-pointer'
            onClick={() => {
              setOtpState({ value: '', error: false, errorMessage: '', context: '' })
              setShowOTPPage(false)
              resetOtpCounter()
            }}
          >
            <Icon IconComponent={ArrowLeft} size='xs'></Icon>
            <span className='font-semibold text-coal-dark cursor-pointer'>{t('go_back')}</span>
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className='flex flex-col gap-16 min-w-[360px] md:min-w-[380px]'>
      <h2 className='text-2xl font-semibold text-zinc-700 items-center text-center'>{t('welcome_to_shopflo')}</h2>

      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(() => validateCredentials())}
          className='flex flex-col gap-8 min-w-[360px] px-4 md:min-w-[380px]'
        >
          <div className='flex flex-col gap-8'>
            <FormField
              control={form.control}
              name='email'
              render={({ field }) => (
                <FormItem>
                  <FormLabel className='text-text-xs-regular text-zinc-500'>{t('email')}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      variant='filled'
                      dimension='lg'
                      className=''
                      placeholder={t('email_placeholder')}
                      error={!!form.formState.errors.email}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className='flex flex-col gap-4'>
            <FormField
              control={form.control}
              name='password'
              render={({ field }) => (
                <FormItem>
                  <FormLabel className='text-text-xs-regular text-zinc-500'>{t('password')}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      variant='filled'
                      dimension='lg'
                      type={showPassword ? 'text' : 'password'}
                      placeholder={t('password_placeholder')}
                      error={!!form.formState.errors.password}
                      endAdornment={
                        <button
                          type='button'
                          onClick={() => setShowPassword(!showPassword)}
                          className='flex items-center justify-center'
                        >
                          {showPassword ? (
                            <Icon IconComponent={EyeSlash} size='sm' />
                          ) : (
                            <Icon IconComponent={Eye} size='sm' />
                          )}
                        </button>
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className='flex justify-end'>
              <p
                className='text-text-xs-semibold text-zinc-600 cursor-pointer'
                onClick={() => router.push('/auth/reset-password')}
              >
                {t('login_page.forgot_password')}
              </p>
            </div>
          </div>

          <Button
            type='submit'
            variant='primary'
            size='lg'
            className='w-full h-11'
            disabled={isLoading}
            loading={isLoading}
          >
            {t('login')}
          </Button>
        </form>
      </Form>
    </div>
  )
}

export default LoginPageV2
